import { useSelector, useDispatch } from "react-redux";
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import CustomDataGrid from "../../../components/dms_common/CustomDataGrid";
import MiniClaimSummary from "../components/MiniClaimSummary";
import { calculateBatchTotals } from "../utils/utils";
import {
  setSaleReturnsCheckedClaimIds,
  setClaimSummaryRows,
  setSaleReturnRows,
  setInitialSaleReturnsRows,
  setSaleReturnsClaimIdsCheckboxHidden,
  setMonth,
  setSelectedClaimSubType,
  setNonEligible
} from "../../../stateManagement/actions/claims";
import { getSaleReturnsColumnsMetaData, SaleReturnRow } from "../utils/CreateClaimUtils";
import { getReturnsData, getAvailableCreditForMultiple, getClaims } from "../../../services/claimService";
import { fetchSalesReturnData } from "../../SalesReturn/SalesReturnService";
import { navigateToSalesReturnDetails } from "../../SalesReturn/SalesReturnUtils";
import { getFormattedDateRange, getCurrentDateTime, getLast12Months } from "../../../utils/calenderUtils";
import { getConfigData } from "../../../services/configurationService";
import { getMetaData } from "../../../services/uiConfigService";
import { getDataFromLocalDb } from "../../../services/dbService";
import { Loader } from "../../../components/loader/Loader";
import { openPopup } from "../../../stateManagement/actions/popupActions";
import { formatDecimal } from "../../../components/dms_common/DmsUtils";
import "./PurchaseReturns.css";
import { logLoadTrace } from "../../../../src/firebase";

const SaleReturns = () => {
  const { saleReturnsCheckedClaimIds, saleReturnsRows, month, saleReturnsClaimIdsCheckboxHidden, initialSaleReturnsRows, nonEligible } = useSelector(
    (state: any) => state.claims
  );
  const [allowZeroCreditNoteClaims, setAllowZeroCreditNoteClaims] = useState(false);
  const [loading, setLoading] = useState(false);
  const [columnsMetaData, setColumnsMetaData] = useState<any>(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const ConfigDictionary = JSON.parse(localStorage.getItem("ConfigDictionary") ?? "{}");
  const { currency, otherUnitLabel, displayOtherUnitName } = ConfigDictionary;

  const filterBadReasonsFromBatch = useCallback((batch: any, goodReasonsSet: Set<string>) => {
    if (!batch.reasonDetails) return batch;

    const filteredReasonDetails = batch.reasonDetails.filter(
      (reason: any) => !goodReasonsSet.has(reason.reason.toLowerCase())
    );

    if (filteredReasonDetails.length === 0) return null;

    const newTotals = filteredReasonDetails.reduce(
      (totals: any, reason: any) => {
        totals.caseQty += reason.caseQty || 0;
        totals.pieceQty += reason.pieceQty || 0;
        totals.otherQty += reason.otherQty || 0;
        return totals;
      },
      { caseQty: 0, pieceQty: 0, otherQty: 0 }
    );

    return {
      ...batch,
      caseQty: newTotals.caseQty,
      pieceQty: newTotals.pieceQty,
      otherQty: newTotals.otherQty,
      reasonDetails: filteredReasonDetails
    };
  }, []);

  const processReturnsData = (returnsData: any[], goodReasons: string[]) => {
    const goodReasonsSet = new Set(goodReasons.map(r => r.toLowerCase()));
    const returnsDataBySkuCode: Record<string, any[]> = {};

    returnsData.forEach((feature: any) => {
      const { invoiceNumber, loginId, referenceNumber } = feature;
      feature.salesDetails.forEach((detail: any) => {
        const filteredBatchIds = detail.batchIds
          ?.map((batch: any) => filterBadReasonsFromBatch(batch, goodReasonsSet))
          .filter(Boolean);

        if (!filteredBatchIds || filteredBatchIds.length === 0) return;

        const skuCode = detail.skuCode;
        if (!returnsDataBySkuCode[skuCode]) {
          returnsDataBySkuCode[skuCode] = [];
        }

        returnsDataBySkuCode[skuCode].push({
          ...detail,
          batchIds: filteredBatchIds,
          invoiceNumber,
          loginId,
          referenceNumber
        });
      });
    });

    return returnsDataBySkuCode;
  };

  const processClaimsData = (claimsData: any[], returnsDataBySkuCode: Record<string, any[]>) => {
    const claimTotalValueMap: Record<string, Record<string, number>> = {};

    if (claimsData.length > 0) {
      Object.keys(returnsDataBySkuCode).forEach(skuCode => {
        claimTotalValueMap[skuCode] = {};
        returnsDataBySkuCode[skuCode].forEach(entry => {
          claimTotalValueMap[skuCode][entry.invoiceNumber] = 0;
        });
      });

      Object.values(returnsDataBySkuCode).forEach((salesEntries) => {
        salesEntries.forEach((entry: any) => {
          claimsData.forEach((claim: any) => {
            claim.claimDetails.forEach((claimDetail: any) => {
              if (
                (claim.status === 'REQUESTED' ||
                  claim.status === 'APPROVED' ||
                  (claim.status === 'PARTIALLY_APPROVED' && claimDetail.approvedAmount > 0)) &&
                claimDetail.referenceNumber === entry.invoiceNumber &&
                claimDetail.skuCode === entry.skuCode
              ) {
                claimTotalValueMap[entry.skuCode][entry.invoiceNumber] +=
                  parseFloat(claimDetail.claimedAmount) || 0;

                entry.batchIds.forEach((batch: any) => {
                  if (batch.batchId === claimDetail.batchCode) {
                    batch.reasonDetails.forEach((reason: any) => {
                      if (reason.reason === claimDetail.remark) {
                        const claimedQty = claimDetail.claimedQty.split('.').map(Number);
                        reason.caseQty = Math.max(0, reason.caseQty - claimedQty[0]);
                        reason.pieceQty = Math.max(0, reason.pieceQty - claimedQty[1]);
                        reason.otherQty = Math.max(0, reason.otherQty - claimedQty[2]);

                        const { totalCaseQty, totalPieceQty, totalOtherQty } = calculateBatchTotals(batch);
                        batch.caseQty = totalCaseQty;
                        batch.pieceQty = totalPieceQty;
                        batch.otherQty = totalOtherQty;
                      }
                    });
                  }
                });
              }
            });
          });
        });
      });
    }

    return claimTotalValueMap;
  };

  const filterNonZeroReturns = (returnsDataBySkuCode: Record<string, any[]>) => {
    const filteredReturnsDataBySkuCode: Record<string, any[]> = {};

    Object.entries(returnsDataBySkuCode).forEach(([skuCode, salesEntries]) => {
      const filteredEntries = salesEntries.filter((entry: any) => {
        if (!entry.batchIds || entry.batchIds.length === 0) return false;

        return entry.batchIds.some((batch: any) => {
          const { totalCaseQty, totalPieceQty, totalOtherQty } = calculateBatchTotals(batch);
          return totalCaseQty !== 0 || totalPieceQty !== 0 || totalOtherQty !== 0;
        });
      });

      if (filteredEntries.length > 0) {
        filteredReturnsDataBySkuCode[skuCode] = filteredEntries;
      }
    });

    return filteredReturnsDataBySkuCode;
  };

  const fetchCreditData = async (allReturnIds: string[]) => {
    const returnOrderCreditMap: Record<string, number> = {};
    const BATCH_SIZE = 50;
    try {
      for (let i = 0; i < allReturnIds.length; i += BATCH_SIZE) {
        const batch = allReturnIds.slice(i, i + BATCH_SIZE);
        const batchReturnIdsString = batch.join(',');

        const creditResponse = await getAvailableCreditForMultiple(batchReturnIdsString);

        creditResponse.forEach((credit: any) => {
          returnOrderCreditMap[credit.returnId] = credit.amount || 0;
        });

        batch.forEach(returnId => {
          if (!(returnId in returnOrderCreditMap)) {
            returnOrderCreditMap[returnId] = 0;
          }
        });
      }
    } catch (error) {
      console.error("Error fetching credit data:", error);
      allReturnIds.forEach(returnId => {
        returnOrderCreditMap[returnId] = 0;
      });
    }

    return returnOrderCreditMap;
  };

  const createRowsData = (
    filteredReturnsDataBySkuCode: Record<string, any[]>,
    returnOrderCreditMap: Record<string, number>,
    claimTotalValueMap: Record<string, Record<string, number>>,
    products: any
  ): SaleReturnRow[] => {
    return Object.entries(filteredReturnsDataBySkuCode).map(([skuCode, salesEntries], idx) => {
      let skuDescription = "N/A";
      const batchCode = salesEntries[0]?.batchCode || "";
      let ptdCs = 0, ptdPc = 0, ptdOther = 0;
      let customOtherUnitLabel = otherUnitLabel;

      if (displayOtherUnitName) {
        const product = products[skuCode];
        if (product?.otherUnitName && product.otherUnitName.length > 0) {
          customOtherUnitLabel = product.otherUnitName;
        }
      }
      for (const entry of salesEntries) {
        if (skuDescription === "N/A" && entry?.productInfo?.skuDescription) { skuDescription = entry.productInfo.skuDescription; }
        if (entry.casePrice !== 0) { ptdCs = entry.casePrice; }
        if (entry.price !== 0) { ptdPc = entry.price; }
        if (entry.otherUnitPrice !== 0) { ptdOther = entry.otherUnitPrice; }
      }

      const returnOrderNo: Record<string, any> = {};
      const invoicesSet = new Set<string>();
      let totalCreditGiven = 0;
      let totalClaimAmount = 0;
      let totalClaimedValue = 0;
      let totalCases = 0;
      let totalPieces = 0;
      let totalOthers = 0;

      salesEntries.forEach((entry: any) => {
        const creditAmount = returnOrderCreditMap[entry.invoiceNumber] || 0;
        returnOrderNo[entry.invoiceNumber] = { batchIds: entry.batchIds, loginId: entry.loginId, creditGiven: creditAmount };
        totalCreditGiven += Number(returnOrderCreditMap[entry.invoiceNumber]) || 0;
        if (entry.referenceNumber) {
          invoicesSet.add(entry.referenceNumber);
        }
        let entryCases = 0;
        let entryPieces = 0;
        let entryOthers = 0;

        entry.batchIds.forEach((batch: any) => {
          const { totalCaseQty, totalPieceQty, totalOtherQty } = calculateBatchTotals(batch);
          entryCases += totalCaseQty;
          entryPieces += totalPieceQty;
          entryOthers += totalOtherQty;
        });

        totalCases += entryCases;
        totalPieces += entryPieces;
        totalOthers += entryOthers;

        const entryClaimAmount =
          (entryCases * Math.abs(entry.casePrice || ptdCs)) +
          (entryPieces * Math.abs(entry.price || ptdPc)) +
          (entryOthers * Math.abs(entry.otherUnitPrice || ptdOther));

        totalClaimAmount += entryClaimAmount;

        if (claimTotalValueMap[skuCode] && claimTotalValueMap[skuCode][entry.invoiceNumber]) {
          totalClaimedValue += claimTotalValueMap[skuCode][entry.invoiceNumber];
        }
      });

      return {
        id: `${month}-${idx + 1}`,
        skuCode,
        skuName: skuDescription,
        returnOrderNo,
        invoices: Array.from(invoicesSet),
        batchCode,
        cases: totalCases,
        pieces: totalPieces,
        others: totalOthers,
        totalCreditGiven: totalCreditGiven === 0 ? "-" : `${currency} ${formatDecimal(totalCreditGiven)}`,
        totalClaimAmount,
        ptdCs: Math.abs(ptdCs),
        ptdPc: Math.abs(ptdPc),
        ptdOther: Math.abs(ptdOther),
        initialCases: totalCases,
        initialPieces: totalPieces,
        initialOthers: totalOthers,
        isNonEligible: nonEligible?.includes(skuCode) || false,
        otherUnitName: customOtherUnitLabel
      };
    });
  };

  const init = async () => {
    setLoading(true);
    try {
      const [formattedStartDate, formattedEndDate] = getFormattedDateRange(month);
      if (!formattedStartDate || !formattedEndDate) return;

      const claimsStartDate = formattedStartDate.replace("%", "T");
      let claimsEndDate = getCurrentDateTime(new Date(), true).replace("%", "T");

      let products = {};
      if (displayOtherUnitName) {
        const dbResult = await getDataFromLocalDb("Product");
        products = dbResult?.data?.[0]?.data ?? {};
      }

      const [returnsResponse, claimsResponse, configDataResponse, stockReturnConfig] = await Promise.allSettled([
        getReturnsData(formattedStartDate.replace("%", " "), formattedEndDate.replace("%", " ")),
        getClaims(claimsStartDate, claimsEndDate, "SaleReturns"),
        getConfigData("dms_configuration", "allowZeroCreditNoteClaims"),
        getMetaData("stockConfig", "stockReturnConfig")
      ]);

      const returnsData = returnsResponse.status === "fulfilled" ? returnsResponse.value : [];
      const claimsData = claimsResponse.status === "fulfilled" ? claimsResponse.value : [];
      const allowZeroCreditNoteClaims = configDataResponse.status === "fulfilled" ? configDataResponse.value[0]?.configValue : [];
      const stockConfig = stockReturnConfig.status === "fulfilled" ? stockReturnConfig.value : [];
      const goodReasonsConfig = (stockConfig.find((item: any) => item.name === "goodReasons") || {}).value || [];

      setAllowZeroCreditNoteClaims(allowZeroCreditNoteClaims);

      const columnMetaData = await getSaleReturnsColumnsMetaData( nonEligible?.length > 0, allowZeroCreditNoteClaims );
      setColumnsMetaData(columnMetaData);

      const returnsDataBySkuCode = processReturnsData(returnsData, goodReasonsConfig);
      const claimTotalValueMap = processClaimsData(claimsData, returnsDataBySkuCode);
      const filteredReturnsDataBySkuCode = filterNonZeroReturns(returnsDataBySkuCode);

      const allReturnIds = Array.from(new Set(
        Object.values(filteredReturnsDataBySkuCode)
          .flatMap(salesEntries => salesEntries.map(entry => entry.invoiceNumber))
          .filter(Boolean)
      ));

      const returnOrderCreditMap = await fetchCreditData(allReturnIds);

      const updatedSaleReturnsRows = createRowsData(filteredReturnsDataBySkuCode, returnOrderCreditMap, claimTotalValueMap, products);

      dispatch(setInitialSaleReturnsRows(updatedSaleReturnsRows || []));
      const mergedRows = updatedSaleReturnsRows.map((fetchedRow) => {
        const editedRow = saleReturnsRows.find((row: SaleReturnRow) => row.id === fetchedRow.id);
        return editedRow ? { ...fetchedRow, ...editedRow, isNonEligible: nonEligible?.includes(fetchedRow.skuCode) || false } : fetchedRow;
      });

      let nonSelectableRows: string[] = [];
      if (!allowZeroCreditNoteClaims) {
        nonSelectableRows = mergedRows.filter((row) => row.totalCreditGiven === '-').map((row) => row.id);
      }

      const zeroClaimAmountRows = mergedRows
      .filter((row) => row.totalClaimAmount === 0)
      .map((row) => row.id);
      nonSelectableRows = [...nonSelectableRows, ...zeroClaimAmountRows];

      const allSelectableRowIds = mergedRows
      .filter((row) => !nonSelectableRows.includes(row.id))
      .map((row) => row.id);

      mergedRows.sort((a, b) => b.totalClaimAmount - a.totalClaimAmount);
      dispatch(setSaleReturnRows(mergedRows || []));

      const previousCheckedIds = saleReturnsCheckedClaimIds;
      const filteredCheckedIds = previousCheckedIds.filter((id: any) => allSelectableRowIds.includes(id));
      dispatch(setSaleReturnsCheckedClaimIds(filteredCheckedIds.length ? filteredCheckedIds : allSelectableRowIds));
      dispatch(setSaleReturnsClaimIdsCheckboxHidden(nonSelectableRows));
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleReturnOrderNoNavigate = async (item: string) => {
    try {
      setLoading(true);
      const data = await fetchSalesReturnData(undefined, undefined, item);
      if (data.length > 0) {
        await navigateToSalesReturnDetails("view", data[0], navigate, "Create New Claim");
      }
      else {
        openPopup("Error", "Failed to fetch sales return data");
      }
    } catch (error) {
      openPopup("Error", "Failed to fetch sales return data");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (month) {
      logLoadTrace(init, "claims_sale_returns_page_load_time");
    }
  }, [month]);

  useEffect(() => {
    const updatedClaimSummaryRows = saleReturnsRows.filter((obj: any) =>
      saleReturnsCheckedClaimIds.includes(obj.id)
    );
    dispatch(setClaimSummaryRows(updatedClaimSummaryRows));
  }, [saleReturnsCheckedClaimIds, saleReturnsRows]);

  useEffect(() => {
    return () => {
      if (!window.location.pathname.includes('saleReturns')) {
        dispatch(setSaleReturnRows([]));
        dispatch(setSaleReturnsCheckedClaimIds([]));
        const currentMonth = getLast12Months()[0];
        dispatch(setMonth(currentMonth));
        dispatch(setSelectedClaimSubType(""));
        dispatch(setNonEligible([]));
      }
    };
  }, []);

  const handleSetCheckedIdArray = (ids: any[]) => {
    const validIds = ids.filter((id) => {
      const row = saleReturnsRows.find((row: SaleReturnRow) => row.id === id);
      return row && (allowZeroCreditNoteClaims || row.totalCreditGiven !== 0) && row.totalClaimAmount > 0;;
    });
    dispatch(setSaleReturnsCheckedClaimIds(validIds));
  };

  return (
    <div className="claims_table_container">
      {loading ? (
        <Loader />
      ) : (
        <>
          <MiniClaimSummary rows={saleReturnsRows} />
          {columnsMetaData && (
            <CustomDataGrid
              columnsMetaData={columnsMetaData}
              rows={saleReturnsRows}
              isCheckboxSelection={true}
              insideSaleReturns={true}
              tableWidth={0.80}
              checkedIdArray={saleReturnsCheckedClaimIds}
              setCheckedIdArray={handleSetCheckedIdArray}
              rowIdsWithCheckboxHidden={saleReturnsClaimIdsCheckboxHidden}
              redirectUrl="view"
              redirectUrlFunction={handleReturnOrderNoNavigate}
              enableRowClickRedirect={false}
            />
          )}
        </>
      )}
    </div>
  );
};

export default SaleReturns;
